<template>
  <q-btn
    round
    icon="more_vert"
    class="q-px-sm"
    :outline="canEdit && report.elementChangeCount > 0"
  >
    <q-badge
      v-if="canEdit && report.elementChangeCount > 0"
      color="warning"
      floating
      :label="report.elementChangeCount"
    />
    <q-menu
      bordered
      side="right"
      overlay
    >
      <q-list>
        <q-item
          v-close-popup
          clickable
          @click="report.runReport()"
        >
          <q-item-section avatar>
            <q-icon name="refresh" />
          </q-item-section>
          <q-item-section>
            Reload
          </q-item-section>
        </q-item>

        <q-item
          v-close-popup
          clickable
          :disable="!report.reportStatus.done"
          @click="report.email = currentUser.email; showEmailDialog = true"
        >
          <q-item-section avatar>
            <q-icon name="email" />
          </q-item-section>
          <q-item-section>
            Email
          </q-item-section>
          <q-tooltip v-if="!report.reportStatus.done">
            Option unavailable while report is running
          </q-tooltip>
        </q-item>

        <q-item
          v-close-popup
          clickable
          :disable="report.dirty || report.isLocal || !canCopyToClipboard"
          @click="copyPublicLink"
        >
          <q-item-section avatar>
            <q-icon name="link" />
          </q-item-section>
          <q-item-section>
            Copy Public Link
          </q-item-section>
          <q-tooltip v-if="!canCopyToClipboard">
            Copying to clipboard requires a secure connection (HTTPS)
          </q-tooltip>
        </q-item>

        <q-item
          v-close-popup
          clickable
          :disable="report.dirty || report.isLocal || !canCopyToClipboard"
          @click="copyPublicExportLink"
        >
          <q-item-section avatar>
            <q-icon name="link" />
          </q-item-section>
          <q-item-section>
            Copy Public Export Link
          </q-item-section>
          <q-tooltip v-if="!canCopyToClipboard">
            Copying to clipboard requires a secure connection (HTTPS)
          </q-tooltip>
        </q-item>

        <q-item
          v-close-popup
          clickable
          :disable="!report.reportStatus.done"
          @click="openPrintView()"
        >
          <q-item-section avatar>
            <q-icon name="print" />
          </q-item-section>
          <q-item-section>
            Print
          </q-item-section>
          <q-tooltip v-if="!report.reportStatus.done">
            Option unavailable while report is running
          </q-tooltip>
        </q-item>

        <q-item
          v-close-popup
          clickable
          :disable="!report.reportStatus.done"
          @click="saveAsPdf()"
        >
          <q-item-section avatar>
            <q-icon name="picture_as_pdf" />
          </q-item-section>
          <q-item-section>
            Save as PDF
          </q-item-section>
          <q-tooltip v-if="!report.reportStatus.done">
            Option unavailable while report is running
          </q-tooltip>
        </q-item>

        <!-- <q-item
          clickable
          @click="report.runReport()"
        >
          <q-item-section avatar>
            <q-icon name="image" />
          </q-item-section>
          <q-item-section>
            Save as Image
          </q-item-section>
        </q-item> -->

        <q-item
          v-close-popup
          clickable
          :disable="!report.reportStatus.done"
          @click="downloadCombinedCsv()"
        >
          <q-item-section avatar>
            <q-icon name="download" />
          </q-item-section>
          <q-item-section>
            Download Raw Data
          </q-item-section>
          <q-tooltip v-if="!report.reportStatus.done">
            Option unavailable while report is running
          </q-tooltip>
        </q-item>
        <q-item
          v-close-popup
          clickable
          :disable="!report.reportStatus.done"
          @click="downloadCombinedCsv(true)"
        >
          <q-item-section avatar>
            <q-icon name="download" />
          </q-item-section>
          <q-item-section>
            Download Raw Data (including totals row)
          </q-item-section>
          <q-tooltip v-if="!report.reportStatus.done">
            Option unavailable while report is running
          </q-tooltip>
        </q-item>

        <q-separator />

        <q-item
          v-close-popup
          clickable
          @click="report.revalidate()"
        >
          <q-item-section avatar>
            <q-icon name="refresh" />
          </q-item-section>
          <q-item-section>
            Revalidate
          </q-item-section>
        </q-item>
        <q-item
          v-close-popup
          clickable
          @click="report.forceRecompute()"
        >
          <q-item-section avatar>
            <q-icon name="cached" />
          </q-item-section>
          <q-item-section>
            Force Recompute
          </q-item-section>
        </q-item>
        <q-item
          v-if="currentUser.superUser"
          v-close-popup
          clickable
          @click="report.forceRecomputeData()"
        >
          <q-item-section avatar>
            <q-icon name="warning" />
          </q-item-section>
          <q-item-section>
            Force Recompute Data
          </q-item-section>
          <q-tooltip>
            Use very carefully. This will force the report to recompute the data from scratch, which can take a very
            long time.
          </q-tooltip>
        </q-item>

        <template v-if="canEdit">
          <q-separator />
          <q-item
            v-close-popup
            clickable
          >
            <q-item-section>
              <q-toggle
                v-model="editing"
                :label="'Edit ' + report.scopeType + ' Report'"
                @update:model-value="val => $emit('edit', val)"
              />
            </q-item-section>
          </q-item>
        </template>
        <template v-if="canEdit && editing">
          <q-item
            v-if="editing"
            v-close-popup
            clickable
            @click="$emit('edit-report')"
          >
            <q-item-section avatar>
              <q-icon name="edit" />
            </q-item-section>
            <q-item-section>
              Report Setting
            </q-item-section>
          </q-item>
          <q-item
            v-if="editing"
            v-close-popup
            clickable
            @click="$refs.printExportDialog.show(report)"
          >
            <q-item-section avatar>
              <q-icon name="print" />
            </q-item-section>
            <q-item-section>
              Print and Export Settings
            </q-item-section>
          </q-item>
          <q-item
            v-if="editing && canEditAtlasReport"
            v-close-popup
            clickable
            @click="$emit('add-element')"
          >
            <q-item-section avatar>
              <q-icon name="add" />
            </q-item-section>
            <q-item-section>
              New Element
            </q-item-section>
          </q-item>
          <q-item
            v-if="canEditAtlasReport"
            v-close-popup
            clickable
            :disable="copiedElementIds.length === 0"
            @click.stop="$emit('paste-elements')"
          >
            <q-item-section avatar>
              <q-icon name="content_paste" />
            </q-item-section>
            <q-item-section>
              Paste {{ copiedElementIds.length ? '(' + copiedElementIds.length + ' Elements)' : '' }}
            </q-item-section>
          </q-item>
        </template>
        <template v-if="canEdit && report.elementChangeCount > 0">
          <q-separator />
          <q-item
            v-if="!editing"
            clickable
            @click="editing = true; $emit('edit', true)"
          >
            <q-item-section avatar>
              <q-icon
                name="warning"
                color="warning"
              />
            </q-item-section>
            <q-item-section>
              Unsaved changes
            </q-item-section>
          </q-item>
          <q-item
            v-else
            v-close-popup
          >
            <q-item-section>
              <div class="row q-gutter-md">
                <q-btn
                  class="col"
                  color="negative"
                  size="sm"
                  label="Discard Changes"
                  @click="report.discardChanges()"
                />
                <q-btn
                  class="col"
                  color="positive"
                  size="sm"
                  label="Save Changes"
                  @click="tryToSaveReport()"
                />
              </div>
            </q-item-section>
          </q-item>
        </template>
      </q-list>
    </q-menu>
    <q-dialog v-model="showSaveOrCopyDialog">
      <q-card style="width: 650px;">
        <q-card-section class="row items-center q-mx-xs q-my-md">
          <div class="text-h6">
            Save Report
          </div>
          <div v-if="!report.atlas">
            <p>
              Do you want to save the report directly (everyone sees the changes) or save a personal copy of the
              report
              (only you can see changes)?
            </p>
            <q-card-actions>
              <q-btn
                v-if="!report.atlas"
                v-close-popup
                icon="save"
                label="Save Report"
                title="Save the report (everyone sees changes)"
                color="blue-8"
                @click="report.publishChanges()"
              />
              <q-btn
                v-close-popup
                label="Save a Copy"
                icon="save_as"
                color="grey-4"
                text-color="black"
                title="Save a personal copy of the report (only you can see changes)"
                @click="saveAsCopy()"
              />
            </q-card-actions>
          </div>
          <div v-else>
            <p v-if="currentUser.superUser">
              Do you want to save the essential report directly or save a copy of the report?
            </p>
            <p v-else>
              You are editing an essential report which is global and cannot be edited directly. Would you like to save
              a
              personal copy?
            </p>

            <q-card-actions>
              <q-btn
                v-if="currentUser.superUser"
                v-close-popup
                icon="save"
                label="Save Report"
                title="Save the report with your changes"
                color="blue-8"
                @click="report.publishChanges()"
              />
              <q-btn
                v-close-popup
                icon="save_alt"
                label="Save a Copy"
                color="grey-4"
                text-color="black"
                @click="saveAsCopy()"
              />
            </q-card-actions>
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>
    <q-dialog v-model="showEmailDialog">
      <q-card style="width: 400px;">
        <q-card-section class="row items-center q-mx-xs">
          <div class="text-h6">
            Email Report
          </div>
          <q-space />
          <q-btn
            v-close-popup
            icon="close"
            flat
            round
            dense
          />
        </q-card-section>
        <q-card-section>
          <div class="row q-gutter-col-md items-center">
            <q-input
              ref="email"
              v-model.trim="report.email"
              class="col-9"
              type="email"
              :label="$t('form.labels.emailAddress')"
              autofocus
              bottom-slots
              lazy-rules
              :rules="[
                val => required.$validator(val) || 'Field is required',
                val => email.$validator(val.toLowerCase()) || $t('form.errors.emailNotValid')
              ]"
            />
            <q-checkbox
              v-model="report.includeCsv"
              class="col-3 q-mb-md"
              label="Include CSV"
            >
              <q-tooltip>
                Include a CSV with raw reporting data in the emailed report for richer data inspection.
              </q-tooltip>
            </q-checkbox>
          </div>
        </q-card-section>

        <q-card-actions class="row justify-end q-ma-sm">
          <q-btn
            label="Send Email"
            color="primary"
            :loading="sendingEmail"
            @click="sendEmail()"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <report-print-settings-dialog
      ref="printExportDialog"
      v-model="showPrintExportDialog"
      :report="report"
    />
  </q-btn>
</template>

<script>

import { email, required } from '@vuelidate/validators'
import { exportFile, Notify } from 'quasar'
import { adminRoot } from 'src/config'
import ReportPrintSettingsDialog from 'src/modules/admin/report/ReportPrintSettingsDialog'

export default {
  name: 'ReportContextMenu',
  components: {
    ReportPrintSettingsDialog
  },
  props: {
    report: {
      type: Object,
      required: true
    },
    copiedElementIds: {
      type: Array,
      required: false,
      default () {
        return []
      }
    }
  },
  emits: ['edit', 'edit-report', 'edit-filters', 'add-element', 'paste-elements'],
  data () {
    return {
      showEmailDialog: false,
      showSaveOrCopyDialog: false,
      showPrintExportDialog: false,
      sendingEmail: false,
      editing: false,
      navigationConfirmed: false,
      routerHookReference: null
    }
  },
  context: ['currentUser'],
  configuration: ['currentSite'],
  computed: {
    canEditAtlasReport () {
      return !this.report.atlas || this.currentUser.superUser
    },
    canCopyToClipboard () {
      return window.isSecureContext
    },
    canEdit () {
      return this.report.user || (this.currentUser.admin && (!this.report.standard || this.currentSite.isLibrary))
    }
  },
  watch: {
    'report.elementChangeCount' (newCount, oldCount) {
      // Reset navigation flag when changes are saved (count goes to 0) or when changes are made (count increases)
      if (newCount === 0 || (oldCount > 0 && newCount > oldCount)) {
        this.navigationConfirmed = false
      }
    }
  },
  created () {
    this.setupNavigationGuard()
    _.extend(this, { email, required })
  },
  unmounted () {
    this.destroyNavigationGuard()
  },
  methods: {
    setupNavigationGuard () {
      this.routerHookReference = this.$router.beforeEach((to, from, next) => {
        if (this.report.elementChangeCount > 0 && !this.navigationConfirmed) {
          this.showUnsavedChangesDialog(() => {
            this.navigationConfirmed = true
            next()
          }, () => {
            next(false)
          })
        } else {
          next()
        }
      })

      window.addEventListener('beforeunload', this.preventNavigation)
    },

    destroyNavigationGuard () {
      if (this.routerHookReference) {
        this.routerHookReference()
        this.routerHookReference = null
      }
      window.removeEventListener('beforeunload', this.preventNavigation)
    },

    showUnsavedChangesDialog (onConfirm, onCancel) {
      this.$q.dialog({
        title: 'Unsaved Changes',
        message: 'Are you sure you want to leave? All unsaved changes for the report will be lost.',
        cancel: {
          label: 'Stay',
          color: 'white',
          'text-color': 'black'
        },
        ok: {
          label: 'Leave (Discard Changes)',
          color: 'red-9',
          'text-color': 'white'
        },
        focus: 'cancel'
      }).onOk(() => {
        onConfirm()
      }).onCancel(() => {
        onCancel()
      })
    },

    preventNavigation (e) {
      if (this.report.elementChangeCount > 0 && !this.navigationConfirmed) {
        e.preventDefault()
        e.returnValue = ''
      }
    },
    async saveAsCopy () {
      try {
        const copiedReport = await this.report.saveAsCopy()

        if (copiedReport.id && copiedReport.categoryName) {
          const categoryRoute = _.kebabCase(copiedReport.categoryName)
          const reportRoute = adminRoot + 'reports/' + categoryRoute + '/' + copiedReport.id
          this.navigationConfirmed = true
          this.$router.push(reportRoute)
        }
      } catch (error) {
        console.error('Error saving report copy:', error)
      }
    },
    downloadCombinedCsv (withTotals = false) {
      exportFile(
        `${this.report.fullTitle} - Combined.csv`,
        withTotals ? this.report.combinedCsvWithTotals : this.report.combinedCsv,
        'text/csv'
      )
    },
    async saveAsPdf () {
      // Make a copy so the save doesn't trigger a loading overlay in the view
      const report = this.report.clone()

      // API may retry PDF export several times, each time taking up to 10s.
      // Display a long enough timer to encourage user to wait long enough.
      const notifyDismiss = Notify.create({
        message: 'Creating PDF',
        timeout: 22000,
        progress: true,
        type: 'info',
        position: 'top'
      })

      const errors = await report.downloadPdf()
      notifyDismiss()

      if (errors && errors.length) return

      Notify.create({
        message: 'PDF Creation Complete',
        type: 'info',
        color: 'positive',
        position: 'top'
      })

      const a = document.createElement('a')
      a.href = 'data:application/pdf;base64,' + report.pdfData
      a.download = `${report.fullTitle}.pdf`
      a.click() // Downloaded file

      report.destroy()
    },

    async tryToSaveReport () {
      if (this.report.user) {
        // Save report directly since it belongs to the current user
        this.report.publishChanges()
      } else {
        this.showSaveOrCopyDialog = true
      }
    },

    async sendEmail () {
      this.showEmailDialog = false

      // Make a copy so the save doesn't trigger a loading overlay in the view
      const report = this.report.clone()

      const notifyDismiss = Notify.create({
        message: 'Building Email, Please Wait',
        timeout: 15000,
        progress: true,
        type: 'info',
        position: 'top'
      })

      const errors = await report.sendEmail()
      notifyDismiss()

      if (errors && errors.length) return

      Notify.create({
        message: 'Email Sent!',
        type: 'info',
        color: 'positive',
        position: 'top'
      })
    },
    openPrintView () {
      // We need to pull from window.location instead of $router because our updateRoute method
      // circumvents the $router and it doesn't have the current query parameters
      const url = '#/print_report/' + window.location.hash.split('/').splice(-1)[0] + '&print=true'
      window.open(url, '_blank')
    },
    copyPublicLink () {
      navigator.clipboard.writeText(this.report.publicReportUrl)

      Notify.create({
        message: 'Link copied to clipboard',
        type: 'info',
        position: 'top'
      })
    },
    copyPublicExportLink () {
      navigator.clipboard.writeText(this.report.publicReportExportUrl)

      Notify.create({
        message: 'Link copied to clipboard',
        type: 'info',
        position: 'top'
      })
    },
    resetNavigationFlag () {
      this.navigationConfirmed = false
    }
  }
}
</script>
